// src/pages/Accounting/Receipts/components/ViewDrawer/DrawerSections.tsx

import type React from "react";
import type { Receipt, MatchedTransaction } from "../../types";
import { PALETTE } from "../../constants";
import { formatCurrency, formatDate, totalTaxForReceipt } from "../../utils";

/* TOTALS */
interface TotalsSectionProps {
  receipt: Receipt;
}
export function TotalsSection({ receipt }: TotalsSectionProps) {
  const subtotal = receipt.total - totalTaxForReceipt(receipt.tax);
  const taxEntries: Array<[string, number]> = [];
  if (receipt.tax.gst) taxEntries.push(["GST (5%)", receipt.tax.gst]);
  if (receipt.tax.hst) taxEntries.push(["HST (13%)", receipt.tax.hst]);
  if (receipt.tax.pst) taxEntries.push(["PST", receipt.tax.pst]);
  if (receipt.tax.other) taxEntries.push(["Other tax", receipt.tax.other]);

  return (
    <div style={sectionStyle}>
      <div style={labelStyle}>Totals</div>
      <KV k="Subtotal" v={formatCurrency(subtotal, receipt.currency)} />
      {taxEntries.map(([k, v]) => (
        <KV key={k} k={k} v={formatCurrency(v, receipt.currency)} />
      ))}
      <KV k="Total" v={formatCurrency(receipt.total, receipt.currency)} total />
    </div>
  );
}

/* LINE ITEMS */
interface LineItemsSectionProps {
  receipt: Receipt;
}
export function LineItemsSection({ receipt }: LineItemsSectionProps) {
  if (!receipt.lineItems || receipt.lineItems.length === 0) return null;
  return (
    <div style={sectionStyle}>
      <div style={labelStyle}>Line items</div>
      {receipt.lineItems.map((item, i) => (
        <div key={i} style={lineStyle}>
          <span>{item.name}</span>
          <span style={amountStyle}>
            {formatCurrency(item.amount, receipt.currency)}
          </span>
        </div>
      ))}
    </div>
  );
}

/* MATCHED TRANSACTION */
interface MatchedSectionProps {
  matched: MatchedTransaction | undefined;
  isSuggestion: boolean;
  onOpenInBankTransactions: () => void;
}
export function MatchedSection({
  matched,
  isSuggestion,
  onOpenInBankTransactions,
}: MatchedSectionProps) {
  if (!matched) {
    return (
      <div style={sectionStyle}>
        <div style={labelStyle}>Matched to</div>
        <div
          style={{
            fontSize: 13,
            color: PALETTE.ink3,
            fontWeight: 500,
            padding: "4px 0",
          }}
        >
          No bank transaction found yet. Vela will keep watching.
        </div>
      </div>
    );
  }

  return (
    <div style={sectionStyle}>
      <div style={labelStyle}>
        {isSuggestion ? "Suggested match" : "Matched to"}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "6px 0",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
          <div style={{ color: PALETTE.ink3, fontSize: 11.5, fontWeight: 500 }}>
            {formatDate(matched.date)} &middot; {matched.bankName}
          </div>
          <div
            style={{
              color: PALETTE.ink,
              fontWeight: 600,
              marginTop: 2,
              fontSize: 13,
            }}
          >
            {matched.description}
          </div>
        </div>
        <div
          style={{
            color: PALETTE.ink,
            fontWeight: 600,
            fontVariantNumeric: "tabular-nums",
            fontSize: 13,
          }}
        >
          {formatCurrency(matched.amount, matched.currency)}
        </div>
      </div>
      <button
        type="button"
        onClick={onOpenInBankTransactions}
        style={{
          background: "transparent",
          border: 0,
          padding: 0,
          marginTop: 6,
          fontSize: 12,
          color: PALETTE.velaDeep,
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        Open transaction in Bank transactions &rarr;
      </button>
    </div>
  );
}

/* META: category + notes */
interface MetaSectionProps {
  receipt: Receipt;
}
export function MetaSection({ receipt }: MetaSectionProps) {
  return (
    <div style={sectionStyle}>
      <div style={labelStyle}>Category &amp; notes</div>
      <KV k="Category" v={receipt.category || "Uncategorized"} />
      <KV k="Notes" v={receipt.notes || "None"} muted={!receipt.notes} />
    </div>
  );
}

/* Reusables */
function KV({
  k,
  v,
  total,
  muted,
}: {
  k: string;
  v: string;
  total?: boolean;
  muted?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        padding: "4px 0",
        ...(total
          ? {
              paddingTop: 10,
              marginTop: 6,
              borderTop: `1px solid ${PALETTE.line}`,
            }
          : null),
      }}
    >
      <span
        style={{
          color: PALETTE.ink2,
          fontWeight: total ? 700 : 500,
          fontSize: total ? 15 : 13,
        }}
      >
        {k}
      </span>
      <span
        style={{
          color: muted ? PALETTE.ink3 : PALETTE.ink,
          fontWeight: total ? 700 : muted ? 500 : 600,
          fontSize: total ? 15 : 13,
          fontVariantNumeric: "tabular-nums",
          textAlign: "right",
          maxWidth: "60%",
        }}
      >
        {v}
      </span>
    </div>
  );
}

const sectionStyle: React.CSSProperties = {
  padding: "14px 0",
  borderTop: `1px solid ${PALETTE.lineSoft}`,
};

const labelStyle: React.CSSProperties = {
  fontSize: 10.5,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: PALETTE.ink2,
  fontWeight: 700,
  marginBottom: 8,
};

const lineStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  padding: "4px 0",
  fontSize: 12.5,
  color: PALETTE.ink2,
  fontWeight: 500,
};

const amountStyle: React.CSSProperties = {
  color: PALETTE.ink,
  fontWeight: 500,
  fontVariantNumeric: "tabular-nums",
};